import React, { useEffect, useState } from 'react';
import { api, formatApiError, resolveImage } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import SEO from '@/components/dubinko/SEO';
import {
  Save, Settings, ListChecks, Mail, FileText, Calendar, MapPin,
  Users, Star, Globe, Search, Bell, Send, Trash2, Plus, Check, X, Bot, Phone, Image as ImageIcon, Megaphone,
  BarChart3, TrendingUp, Smartphone, Monitor, Tablet, Activity, MousePointerClick, Eye, BookOpen, Pencil
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, PieChart, Pie, Cell
} from 'recharts';
import ImagePicker from '@/components/dubinko/ImagePicker';

const TABS = [
  { id: 'bookings', label: 'Rezervacije', icon: ListChecks, tid: ADMIN.tabBookings },
  { id: 'stats', label: 'Statistika', icon: BarChart3, tid: ADMIN.tabStats },
  { id: 'customers', label: 'Klijenti', icon: Users, tid: ADMIN.tabCustomers },
  { id: 'cmessages', label: 'Pitanja', icon: Mail, tid: ADMIN.tabCustomerMessages },
  { id: 'assistant', label: 'Asistent', icon: Bot, tid: ADMIN.tabAssistant },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, tid: 'admin-tab-marketing' },
  { id: 'blog', label: 'Blog / Saveti', icon: BookOpen, tid: ADMIN.tabBlog },
  { id: 'reviews', label: 'Recenzije', icon: Star, tid: ADMIN.tabReviews },
  { id: 'areas', label: 'Područja', icon: MapPin, tid: ADMIN.tabAreas },
  { id: 'quotes', label: 'Ponude', icon: FileText, tid: ADMIN.tabQuotes },
  { id: 'messages', label: 'Kontakt', icon: Mail, tid: ADMIN.tabMessages },
  { id: 'content', label: 'Sadržaj', icon: Settings, tid: ADMIN.tabContent },
  { id: 'images', label: 'Slike & Usluge', icon: ImageIcon, tid: 'admin-tab-images' },
  { id: 'seo', label: 'SEO & Google', icon: Globe, tid: ADMIN.tabSEO },
];

const STATUS_OPTIONS = [
  { v: 'pending', l: 'Na čekanju' },
  { v: 'confirmed', l: 'Potvrđeno' },
  { v: 'completed', l: 'Završeno' },
  { v: 'cancelled', l: 'Otkazano' },
];

const PAYMENT_OPTIONS = [
  { v: 'unpaid', l: 'Nije plaćeno' },
  { v: 'paid', l: 'Plaćeno' },
  { v: 'refunded', l: 'Vraćeno' },
];

const SERVICE_LABEL = { residential: 'Stan/kuća', commercial: 'Poslovni', carpet: 'Tepisi/tapacirung' };

export default function AdminPage() {
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cmessages, setCmessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [areas, setAreas] = useState([]);
  const [content, setContent] = useState(null);
  const [operator, setOperator] = useState(null);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');

  const refresh = async () => {
    try {
      const [b, q, m, c, cm, rv, ar, ct, op, ld] = await Promise.all([
        api.get('/admin/bookings'),
        api.get('/admin/quotes'),
        api.get('/admin/messages'),
        api.get('/admin/customers'),
        api.get('/admin/customer-messages'),
        api.get('/admin/reviews'),
        api.get('/admin/areas'),
        api.get('/site/content'),
        api.get('/operator/status'),
        api.get('/admin/chat-leads'),
      ]);
      setBookings(b.data); setQuotes(q.data); setMessages(m.data);
      setCustomers(c.data); setCmessages(cm.data); setReviews(rv.data);
      setAreas(ar.data); setContent(ct.data);
      setOperator(op.data); setLeads(ld.data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <main data-testid={ADMIN.root}>
      <SEO title="Admin panel" description="Upravljanje Dubinko" path="/admin" />
      <section className="section-pad aurora-bg grain">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <span className="tag-pill">Admin panel · CRM</span>
              <h1 className="font-display text-4xl md:text-5xl font-extrabold mt-4">Dubinko upravljanje</h1>
              <p className="text-slate-600 mt-3">Rezervacije, klijenti, recenzije, područja, sadržaj i SEO.</p>
            </div>
            <a
              href="/dubinko-uputstvo.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-download-guide"
              className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold self-start lg:self-auto"
            >
              <FileText className="w-4 h-4" /> Preuzmi uputstvo (PDF)
            </a>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map((t) => {
              const I = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} data-testid={t.tid} onClick={() => setTab(t.id)}
                  className={`px-4 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 transition ${
                    active ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400'
                  }`}>
                  <I className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>

          {error && <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}

          {tab === 'bookings' && <BookingsTable bookings={bookings} onRefresh={refresh} />}
          {tab === 'stats' && <StatisticsTab />}
          {tab === 'customers' && <CustomersTab customers={customers} onRefresh={refresh} />}
          {tab === 'cmessages' && <CustomerMessagesTab messages={cmessages} onRefresh={refresh} />}
          {tab === 'assistant' && operator && <AssistantTab operator={operator} setOperator={setOperator} leads={leads} onRefresh={refresh} />}
          {tab === 'marketing' && content && <MarketingTab content={content} setContent={setContent} />}
          {tab === 'blog' && <BlogTab />}
          {tab === 'reviews' && <ReviewsTab reviews={reviews} onRefresh={refresh} />}
          {tab === 'areas' && <AreasTab areas={areas} onRefresh={refresh} />}
          {tab === 'quotes' && <QuotesTab quotes={quotes} />}
          {tab === 'messages' && <ContactMessagesTab messages={messages} />}
          {tab === 'content' && content && <ContentTab content={content} setContent={setContent} />}
          {tab === 'images' && content && <ImagesTab content={content} setContent={setContent} />}
          {tab === 'seo' && content && <SeoTab content={content} setContent={setContent} />}
        </div>
      </section>
    </main>
  );
}

// ---------- Statistics ----------
const RANGES = [
  { v: 'today', l: 'Danas' },
  { v: '7d', l: '7 dana' },
  { v: '30d', l: '30 dana' },
  { v: '90d', l: '90 dana' },
];

const DEVICE_META = {
  mobile: { label: 'Mobilni', icon: Smartphone, color: '#059669' },
  desktop: { label: 'Desktop', icon: Monitor, color: '#2563eb' },
  tablet: { label: 'Tablet', icon: Tablet, color: '#7c3aed' },
};

const REFERRER_LABEL = {
  direct: 'Direktno', google: 'Google', facebook: 'Facebook',
  instagram: 'Instagram', bing: 'Bing', internal: 'Interno', other: 'Ostalo',
};

const PAGE_LABEL = {
  '/': 'Početna', '/usluge': 'Usluge', '/rezervacija': 'Rezervacija',
  '/o-nama': 'O nama', '/kontakt': 'Kontakt', '/recenzije': 'Recenzije', '/podrucja': 'Područja',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'upravo sada';
  if (diff < 3600) return `pre ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `pre ${Math.floor(diff / 3600)} h`;
  return `pre ${Math.floor(diff / 86400)} d`;
}

function StatCard({ icon: Icon, label, value, sub, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="stat-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${tint}1a`, color: tint }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-extrabold leading-none">{value}</div>
          <div className="text-xs text-slate-500 mt-1">{label}</div>
        </div>
      </div>
      {sub && <div className="text-xs text-slate-400 mt-2">{sub}</div>}
    </div>
  );
}

function StatisticsTab() {
  const [rng, setRng] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async (r) => {
    setLoading(true); setErr('');
    try {
      const [s, l] = await Promise.all([
        api.get(`/admin/analytics/summary?rng=${r}`),
        api.get('/admin/analytics/live?limit=40'),
      ]);
      setSummary(s.data); setLive(l.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(rng); }, [rng]);

  const t = summary?.totals || {};
  const timeseries = (summary?.timeseries || []).map((d) => ({
    ...d, label: d.day.slice(5),
  }));
  const topPages = (summary?.top_pages || []).map((p) => ({
    name: PAGE_LABEL[p.path] || p.path, count: p.count,
  }));
  const devices = summary?.devices || { mobile: 0, desktop: 0, tablet: 0 };
  const deviceData = Object.entries(devices)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: DEVICE_META[k]?.label || k, value: v, color: DEVICE_META[k]?.color || '#94a3b8' }));
  const maxFunnel = Math.max(1, ...((summary?.funnel || []).map((f) => f.count)));

  return (
    <div data-testid="admin-stats-panel" className="space-y-6">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2" data-testid={ADMIN.statsRange}>
          {RANGES.map((r) => (
            <button key={r.v} data-testid={`stats-range-${r.v}`} onClick={() => setRng(r.v)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                rng === r.v ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400'
              }`}>{r.l}</button>
          ))}
        </div>
        <button onClick={() => load(rng)} data-testid="stats-refresh"
          className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
          <Activity className="w-4 h-4" /> Osveži
        </button>
      </div>

      <a href="/dubinko-statistika.pdf" target="_blank" rel="noreferrer" data-testid="stats-download-guide"
        className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900">
        <FileText className="w-4 h-4" /> Preuzmi uputstvo za statistiku (PDF)
      </a>

      {err && <div className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{err}</div>}
      {loading && !summary && <div className="text-slate-500 py-8 text-center">Učitavanje statistike…</div>}

      {summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={Eye} label="Pregledi stranica" value={t.pageviews ?? 0} tint="#2563eb" />
            <StatCard icon={Users} label="Jedinstveni posetioci" value={t.unique_visitors ?? 0} tint="#059669" />
            <StatCard icon={Activity} label="Sesije" value={t.sessions ?? 0} tint="#7c3aed" />
            <StatCard icon={MousePointerClick} label="Interakcije (klikovi)" value={t.events ?? 0} tint="#d97706" />
            <StatCard icon={TrendingUp} label="Konverzija (rezervacije)" value={`${t.conversion_rate ?? 0}%`} tint="#db2777" />
          </div>

          {/* Traffic timeseries */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-700" /> Posećenost tokom vremena</h3>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={timeseries} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                  <RTooltip />
                  <Area type="monotone" dataKey="pageviews" name="Pregledi" stroke="#2563eb" fill="url(#pvGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" name="Posetioci" stroke="#059669" fill="url(#visGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top pages */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Eye className="w-4 h-4 text-blue-700" /> Najposećenije stranice</h3>
              {topPages.length === 0 ? <EmptyStat /> : (
                <div style={{ width: '100%', height: Math.max(180, topPages.length * 38) }}>
                  <ResponsiveContainer>
                    <BarChart data={topPages} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <XAxis type="number" hide allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} stroke="#64748b" />
                      <RTooltip />
                      <Bar dataKey="count" name="Pregledi" fill="#2563eb" radius={[0, 6, 6, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Devices */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Smartphone className="w-4 h-4 text-emerald-600" /> Uređaji</h3>
              {deviceData.length === 0 ? <EmptyStat /> : (
                <div className="flex items-center gap-4">
                  <div style={{ width: 160, height: 160 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={deviceData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                          {deviceData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <RTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {deviceData.map((d) => (
                      <div key={d.name} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                        <span className="text-slate-700">{d.name}</span>
                        <span className="font-semibold ml-1">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Top events */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-amber-600" /> Najčešće interakcije</h3>
              {(summary.top_events || []).length === 0 ? <EmptyStat /> : (
                <ul className="space-y-2" data-testid="stats-top-events">
                  {summary.top_events.map((e) => (
                    <li key={e.name} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                      <span className="text-slate-700">{e.label}</span>
                      <span className="font-semibold bg-amber-50 text-amber-700 px-2.5 py-0.5 rounded-full">{e.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Referrers */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-purple-700" /> Izvori posete</h3>
              {(summary.referrers || []).length === 0 ? <EmptyStat /> : (
                <ul className="space-y-2">
                  {summary.referrers.map((r) => (
                    <li key={r.source} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                      <span className="text-slate-700">{REFERRER_LABEL[r.source] || r.source}</span>
                      <span className="font-semibold text-slate-900">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Funnel */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-pink-600" /> Konverzioni levak (od posete do rezervacije)</h3>
            <div className="space-y-3">
              {(summary.funnel || []).map((f, i) => {
                const pct = Math.round((f.count / maxFunnel) * 100);
                const colors = ['#2563eb', '#7c3aed', '#d97706', '#059669'];
                return (
                  <div key={f.stage}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-700">{f.stage}</span>
                      <span className="font-semibold">{f.count}</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: colors[i % colors.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Live feed */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-600" /> Aktivnost uživo</h3>
            {live.length === 0 ? <EmptyStat /> : (
              <ul className="space-y-2 max-h-80 overflow-y-auto" data-testid="stats-live-feed">
                {live.map((e) => {
                  const DIcon = DEVICE_META[e.device]?.icon || Monitor;
                  return (
                    <li key={e.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-2 last:border-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${e.type === 'event' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                      <span className="text-slate-700 flex-1 truncate">{e.label}</span>
                      <DIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-xs text-slate-400 shrink-0">{timeAgo(e.created_at)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyStat() {
  return <div className="text-sm text-slate-400 py-6 text-center">Još nema podataka za izabrani period.</div>;
}

// ---------- Blog / Saveti ----------
const EMPTY_POST = { title: '', excerpt: '', content: '', cover_image: '', published: true };

function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post object or null
  const [form, setForm] = useState(EMPTY_POST);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try { const { data } = await api.get('/admin/blog'); setPosts(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm(EMPTY_POST); setErr(''); };
  const startEdit = (p) => { setEditing(p.id); setForm({ title: p.title, excerpt: p.excerpt || '', content: p.content, cover_image: p.cover_image || '', published: p.published }); setErr(''); };
  const cancel = () => { setEditing(null); setForm(EMPTY_POST); };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      if (editing === 'new') await api.post('/admin/blog', form);
      else await api.patch(`/admin/blog/${editing}`, form);
      await load(); cancel();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Obrisati ovaj članak?')) return;
    try { await api.delete(`/admin/blog/${id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const togglePublish = async (p) => {
    try { await api.patch(`/admin/blog/${p.id}`, { published: !p.published }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-blog-panel">
      <div className="flex items-center justify-between">
        <p className="text-slate-600 text-sm">Objavljujte savete i članke — poboljšava Google rangiranje i poverenje posetilaca.</p>
        {!editing && (
          <button onClick={startNew} data-testid="blog-new-btn"
            className="btn-primary px-4 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Novi članak
          </button>
        )}
      </div>

      {err && <div className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{err}</div>}

      {editing ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="blog-editor">
          <div>
            <label className="text-sm font-semibold text-slate-700">Naslov</label>
            <input data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-blue-700" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Kratak opis (excerpt)</label>
            <input data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-blue-700" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Naslovna slika</label>
            <ImagePicker value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Sadržaj</label>
            <textarea data-testid="blog-content" rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-blue-700 resize-y leading-relaxed" />
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" data-testid="blog-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Objavljeno (vidljivo na sajtu)
          </label>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy || !form.title || !form.content} data-testid="blog-save"
              className="btn-primary px-5 py-2.5 rounded-full font-semibold disabled:opacity-50 inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> {busy ? 'Čuvam…' : 'Sačuvaj članak'}
            </button>
            <button onClick={cancel} className="btn-ghost px-5 py-2.5 rounded-full font-semibold">Otkaži</button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="blog-list">
          {posts.length === 0 ? (
            <div className="text-center text-slate-500 py-10">Još nema članaka. Kliknite «Novi članak».</div>
          ) : posts.map((p) => (
            <div key={p.id} data-testid="blog-row" className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0">
              {p.cover_image
                ? <img src={resolveImage(p.cover_image)} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                : <div className="w-14 h-14 rounded-xl bg-slate-100 shrink-0 flex items-center justify-center"><BookOpen className="w-5 h-5 text-slate-400" /></div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{p.title}</div>
                <div className="text-xs text-slate-500 truncate">/saveti/{p.slug}</div>
              </div>
              <button onClick={() => togglePublish(p)} data-testid="blog-toggle-publish"
                className={`text-xs px-2.5 py-1 rounded-full font-semibold ${p.published ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {p.published ? 'Objavljeno' : 'Skica'}
              </button>
              <button onClick={() => startEdit(p)} data-testid="blog-edit" className="p-2 rounded-lg hover:bg-slate-100" aria-label="Izmeni"><Pencil className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => remove(p.id)} data-testid="blog-delete" className="p-2 rounded-lg hover:bg-red-50" aria-label="Obriši"><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




// ---------- Bookings ----------
function BookingsTable({ bookings, onRefresh }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={ADMIN.bookingsTable}>
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {['Kupac', 'Usluga', 'Termin', 'Adresa', 'Cena (€)', 'Status', 'Plaćanje', 'Napomene', 'Akcije'].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-slate-500 py-8">Nema rezervacija.</td></tr>
            ) : bookings.map((b) => <BookingRow key={b.id} b={b} onSave={onRefresh} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingRow({ b, onSave }) {
  const [status, setStatus] = useState(b.status);
  const [paymentStatus, setPaymentStatus] = useState(b.payment_status || 'unpaid');
  const [price, setPrice] = useState(b.estimated_price ?? '');
  const [paid, setPaid] = useState(b.amount_paid ?? '');
  const [notes, setNotes] = useState(b.admin_notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${b.id}`, {
        status, payment_status: paymentStatus,
        estimated_price: price === '' ? null : Number(price),
        amount_paid: paid === '' ? null : Number(paid),
        admin_notes: notes,
      });
      onSave();
    } finally { setBusy(false); }
  };

  const sendReminder = async () => {
    setBusy(true);
    try { await api.post(`/admin/bookings/${b.id}/reminder`); onSave(); }
    finally { setBusy(false); }
  };
  const sendFollowUp = async () => {
    setBusy(true);
    try { await api.post(`/admin/bookings/${b.id}/follow-up`); onSave(); }
    finally { setBusy(false); }
  };

  return (
    <tr data-testid={ADMIN.bookingRow} className="border-t border-slate-100 align-top">
      <td className="px-4 py-3">
        <div className="font-semibold">{b.customer_name}</div>
        <div className="text-xs text-slate-500">{b.customer_email}{b.customer_phone ? ` · ${b.customer_phone}` : ''}</div>
      </td>
      <td className="px-4 py-3">{SERVICE_LABEL[b.service_type] || b.service_type}<div className="text-xs text-slate-500">{b.property_size}</div></td>
      <td className="px-4 py-3"><Calendar className="w-3.5 h-3.5 inline mr-1 text-blue-700" />{b.preferred_date} {b.preferred_time}</td>
      <td className="px-4 py-3"><MapPin className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />{b.address}</td>
      <td className="px-4 py-3">
        <input type="number" value={price} data-testid={ADMIN.priceInput}
          onChange={(e) => setPrice(e.target.value)}
          className="w-20 px-2 py-1.5 rounded-lg border border-slate-300" />
        <input type="number" placeholder="plaćeno" value={paid} data-testid={ADMIN.paidInput}
          onChange={(e) => setPaid(e.target.value)}
          className="mt-1 w-20 px-2 py-1.5 rounded-lg border border-slate-300" />
      </td>
      <td className="px-4 py-3">
        <select data-testid={ADMIN.statusSelect} value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white">
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </td>
      <td className="px-4 py-3">
        <select data-testid={ADMIN.paymentSelect} value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white">
          {PAYMENT_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </td>
      <td className="px-4 py-3">
        <textarea data-testid={ADMIN.notesInput} rows={2} value={notes}
          onChange={(e) => setNotes(e.target.value)} placeholder="Interno…"
          className="w-44 px-2 py-1.5 rounded-lg border border-slate-300 resize-none" />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1.5">
          <button data-testid={ADMIN.saveBtn} onClick={save} disabled={busy}
            className="btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <Save className="w-3 h-3" /> Sačuvaj
          </button>
          <button data-testid={ADMIN.reminderBtn} onClick={sendReminder} disabled={busy}
            className="btn-ghost px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <Bell className="w-3 h-3" /> Podsetnik{b.reminder_sent_at && ' ✓'}
          </button>
          <button data-testid={ADMIN.followUpBtn} onClick={sendFollowUp} disabled={busy}
            className="btn-ghost px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <Send className="w-3 h-3" /> Pozdrav{b.follow_up_sent_at && ' ✓'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------- Customers ----------
function CustomersTab({ customers, onRefresh }) {
  const [active, setActive] = useState(null);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white border border-slate-200 rounded-3xl p-4" data-testid={ADMIN.customersList}>
        <div className="text-sm font-semibold text-slate-500 px-2 py-1.5">{customers.length} klijenata</div>
        <div className="divide-y divide-slate-100 mt-2">
          {customers.length === 0 ? (
            <div className="text-slate-500 text-sm py-6 text-center">Još uvek nema registrovanih klijenata.</div>
          ) : customers.map((c) => (
            <button key={c.id} data-testid={ADMIN.customerRow} onClick={() => setActive(c.id)}
              className={`w-full text-left px-3 py-3 hover:bg-slate-50 rounded-xl transition ${active === c.id ? 'bg-slate-50' : ''}`}>
              <div className="font-semibold text-sm">{c.name}</div>
              <div className="text-xs text-slate-500">{c.email}</div>
              <div className="text-xs text-slate-500 mt-1">
                {c.bookings_count} rezervacija · plaćeno {Number(c.total_spent || 0).toFixed(0)} €
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2">
        {active ? <CustomerDetail customerId={active} onRefresh={onRefresh} /> : (
          <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-10 text-center text-slate-500">
            Izaberite klijenta sa leve strane da vidite istoriju.
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerDetail({ customerId, onRefresh }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/customers/${customerId}`);
    setData(data);
    setNotes(data.user.admin_notes || '');
  };
  useEffect(() => { load(); }, [customerId]);

  if (!data) return <div className="text-slate-500">Učitavanje…</div>;

  const saveNotes = async () => {
    setBusy(true);
    try { await api.patch(`/admin/customers/${customerId}`, { notes }); await load(); onRefresh(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={ADMIN.customerOpen}>
      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <div className="font-display text-2xl font-bold">{data.user.name}</div>
        <div className="text-sm text-slate-600">{data.user.email} {data.user.phone ? `· ${data.user.phone}` : ''}</div>
        <div className="text-xs text-slate-500 mt-1">Klijent od {new Date(data.user.created_at).toLocaleDateString('sr-RS')}</div>
        <div className="mt-4">
          <label className="text-sm font-semibold text-slate-700">Interne napomene</label>
          <textarea data-testid={ADMIN.customerNotes} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-1.5 w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          <button data-testid={ADMIN.customerNotesSave} onClick={saveNotes} disabled={busy}
            className="btn-primary mt-2 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Save className="w-3.5 h-3.5" /> Sačuvaj napomene
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <div className="font-display font-bold mb-3">Rezervacije ({data.bookings.length})</div>
        {data.bookings.length === 0 ? <div className="text-sm text-slate-500">Nema rezervacija.</div> : (
          <div className="space-y-2">
            {data.bookings.map((b) => (
              <div key={b.id} className="p-3 rounded-xl bg-slate-50 text-sm flex justify-between gap-3">
                <div>
                  <strong>{SERVICE_LABEL[b.service_type]}</strong> · {b.preferred_date} {b.preferred_time}
                  <div className="text-xs text-slate-500">{b.address}, {b.city}</div>
                </div>
                <div className="text-right text-xs">
                  <span className="font-semibold">{b.status}</span>
                  {b.estimated_price && <div className="text-slate-500">{Number(b.estimated_price).toFixed(0)} €</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.reviews.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="font-display font-bold mb-3">Recenzije ({data.reviews.length})</div>
          {data.reviews.map((r) => (
            <div key={r.id} className="p-3 rounded-xl bg-slate-50 text-sm mb-2">
              <div className="flex gap-0.5 text-amber-500">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-500' : ''}`} />)}</div>
              <p className="text-slate-700 mt-1">{r.text}</p>
            </div>
          ))}
        </div>
      )}

      {data.messages.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="font-display font-bold mb-3">Pitanja ({data.messages.length})</div>
          {data.messages.map((m) => (
            <div key={m.id} className="p-3 rounded-xl bg-slate-50 text-sm mb-2">
              <div className="font-semibold">{m.subject}</div>
              <p className="text-slate-700 mt-1 whitespace-pre-wrap">{m.message}</p>
              {m.admin_reply && <div className="mt-2 text-emerald-700 text-xs"><strong>Odgovor:</strong> {m.admin_reply}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Customer messages ----------
function CustomerMessagesTab({ messages, onRefresh }) {
  return (
    <div className="space-y-3" data-testid={ADMIN.cMessagesList}>
      {messages.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center text-slate-500">Nema pitanja.</div>
      ) : messages.map((m) => <CMessageRow key={m.id} m={m} onRefresh={onRefresh} />)}
    </div>
  );
}

function CMessageRow({ m, onRefresh }) {
  const [reply, setReply] = useState(m.admin_reply || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.patch(`/admin/customer-messages/${m.id}`, { reply }); setOpen(false); onRefresh(); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold">{m.subject}</div>
          <div className="text-xs text-slate-500">{m.customer_name} · {m.customer_email}</div>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.status === 'answered' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
          {m.status === 'answered' ? 'Odgovoreno' : 'Otvoreno'}
        </span>
      </div>
      <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{m.message}</p>
      {m.admin_reply && !open && (
        <div className="mt-3 p-3 rounded-xl bg-slate-50 border-l-4 border-emerald-500 text-sm text-slate-800 whitespace-pre-wrap">
          <strong>Odgovor:</strong> {m.admin_reply}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid={ADMIN.cMessageReply} onClick={() => setOpen((v) => !v)}
          className="btn-ghost px-4 py-2 rounded-full text-xs font-semibold">{open ? 'Otkaži' : (m.admin_reply ? 'Izmeni odgovor' : 'Odgovori')}</button>
      </div>
      {open && (
        <div className="mt-3">
          <textarea data-testid={ADMIN.cMessageReplyText} rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          <button data-testid={ADMIN.cMessageReplySend} onClick={submit} disabled={busy || !reply.trim()}
            className="btn-primary mt-2 px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> Pošalji odgovor
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Reviews ----------
function ReviewsTab({ reviews, onRefresh }) {
  const moderate = async (id, status) => {
    await api.patch(`/admin/reviews/${id}`, { status });
    onRefresh();
  };
  return (
    <div className="space-y-3" data-testid={ADMIN.reviewsList}>
      {reviews.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center text-slate-500">Nema recenzija.</div>
      ) : reviews.map((r) => (
        <div key={r.id} data-testid={ADMIN.reviewRow} className="bg-white border border-slate-200 rounded-3xl p-5 flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5 text-amber-500">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-amber-500' : ''}`} />)}</div>
              <div className="font-semibold">{r.customer_name}</div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                r.status === 'hidden' ? 'bg-slate-200 text-slate-700' :
                'bg-amber-100 text-amber-800'
              }`}>
                {r.status === 'approved' ? 'Objavljeno' : r.status === 'hidden' ? 'Sakriveno' : 'Čeka'}
              </span>
            </div>
            <p className="text-sm text-slate-700 mt-2">{r.text}</p>
            <div className="text-xs text-slate-500 mt-2">{new Date(r.created_at).toLocaleString('sr-RS')}</div>
          </div>
          <div className="flex gap-2">
            <button data-testid={ADMIN.reviewApprove} onClick={() => moderate(r.id, 'approved')}
              className="btn-green px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"><Check className="w-3 h-3" /> Odobri</button>
            <button data-testid={ADMIN.reviewHide} onClick={() => moderate(r.id, 'hidden')}
              className="btn-ghost px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"><X className="w-3 h-3" /> Sakrij</button>
          </div>
        </div>
      ))}
    </div>
  );
}


// ---------- Assistant (chat) ----------
const LEAD_STATUS = [
  { v: 'new', l: 'Novo' },
  { v: 'contacted', l: 'Pozvan' },
  { v: 'closed', l: 'Zatvoreno' },
];
const INTENT_LABEL = {
  operator: 'Operater',
  quote: 'Ponuda',
  booking_help: 'Pomoć oko zakazivanja',
  other: 'Ostalo',
};
function AssistantTab({ operator, setOperator, leads, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const saveOperator = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put('/admin/operator/status', { ...operator, ...patch });
      setOperator(data);
      setSavedAt(Date.now());
    } finally { setBusy(false); }
  };

  const setLeadStatus = async (id, status) => {
    await api.patch(`/admin/chat-leads/${id}`, { status });
    onRefresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white border border-slate-200 rounded-3xl p-5">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-700" />
          <h3 className="font-display text-xl font-bold">Dubi asistent</h3>
        </div>
        <p className="text-sm text-slate-600 mt-1">Live chat sa AI i operaterom.</p>

        <div className="mt-5 p-4 rounded-2xl bg-slate-50 flex items-center justify-between">
          <div>
            <div className="font-semibold">Operater dostupan</div>
            <div className="text-xs text-slate-500">{operator.is_online ? 'Klijenti vide zelenu lampicu' : 'Klijenti ostavljaju kontakt'}</div>
          </div>
          <button
            data-testid={ADMIN.operatorToggle}
            onClick={() => saveOperator({ is_online: !operator.is_online })}
            disabled={busy}
            className={`relative w-12 h-7 rounded-full transition ${operator.is_online ? 'bg-emerald-500' : 'bg-slate-300'}`}
            aria-pressed={operator.is_online}>
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${operator.is_online ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="block text-sm font-semibold text-slate-700 mb-1.5">Poruka kada je operater online</span>
            <textarea data-testid={ADMIN.operatorOnlineMsg} rows={2} value={operator.online_message}
              onChange={(e) => setOperator({ ...operator, online_message: e.target.value })}
              className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-slate-700 mb-1.5">Poruka kada nismo online</span>
            <textarea data-testid={ADMIN.operatorAway} rows={3} value={operator.away_message}
              onChange={(e) => setOperator({ ...operator, away_message: e.target.value })}
              className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          </label>
          <button data-testid={ADMIN.operatorSave} onClick={() => saveOperator({})} disabled={busy}
            className="btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Save className="w-3.5 h-3.5" /> Sačuvaj
          </button>
          {savedAt > 0 && <div className="text-xs text-emerald-700">Sačuvano u {new Date(savedAt).toLocaleTimeString()}</div>}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-5" data-testid={ADMIN.leadsList}>
        <h3 className="font-display text-xl font-bold">Lead-ovi iz chat-a ({leads.length})</h3>
        <p className="text-sm text-slate-600 mt-1">Kontakti koje su klijenti ostavili kroz Dubi chat — najnoviji prvi.</p>
        <div className="mt-4 space-y-3">
          {leads.length === 0 ? (
            <div className="text-center text-slate-500 py-8 text-sm">Još uvek nema lead-ova.</div>
          ) : leads.map((l) => (
            <div key={l.id} data-testid={ADMIN.leadRow}
              className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold">{l.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{INTENT_LABEL[l.intent] || l.intent}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === 'new' ? 'bg-amber-100 text-amber-800' : l.status === 'contacted' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'}`}>
                    {LEAD_STATUS.find((s) => s.v === l.status)?.l}
                  </span>
                </div>
                <div className="mt-2 text-sm text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                  <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-blue-700"><Phone className="w-3.5 h-3.5" /> {l.phone}</a>
                  {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-blue-700"><Mail className="w-3.5 h-3.5" /> {l.email}</a>}
                  {l.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.address}</span>}
                </div>
                {l.message && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{l.message}</p>}
                <div className="mt-2 text-[11px] text-slate-500">{new Date(l.created_at).toLocaleString('sr-RS')}</div>
              </div>
              <div>
                <select data-testid={ADMIN.leadStatus} value={l.status}
                  onChange={(e) => setLeadStatus(l.id, e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-sm">
                  {LEAD_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Areas ----------
function AreasTab({ areas, onRefresh }) {
  const [form, setForm] = useState({ name: '', description: '', neighborhoods: '', hero_image: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/admin/areas', form);
      setForm({ name: '', description: '', neighborhoods: '', hero_image: '' });
      onRefresh();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Obrisati područje?')) return;
    await api.delete(`/admin/areas/${id}`); onRefresh();
  };

  const toggle = async (a) => {
    await api.patch(`/admin/areas/${a.id}`, { is_active: !a.is_active });
    onRefresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="bg-white border border-slate-200 rounded-3xl p-5 lg:col-span-1">
        <h3 className="font-display font-bold">Novo područje</h3>
        <div className="mt-3 space-y-3">
          <input data-testid={ADMIN.areaName} required value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            placeholder="Npr. Niš centar"
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
          <textarea data-testid={ADMIN.areaDescription} rows={3} value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            placeholder="Kratak opis (SEO)"
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
          <input value={form.neighborhoods}
            onChange={(e) => setForm({...form, neighborhoods: e.target.value})}
            placeholder="Naselja (npr. Bulevar, Trg, …)"
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
          <input value={form.hero_image}
            onChange={(e) => setForm({...form, hero_image: e.target.value})}
            placeholder="URL hero slike (opciono)"
            className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
          {error && <div className="text-xs text-red-700">{error}</div>}
          <button data-testid={ADMIN.areaCreate} type="submit" disabled={busy}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold disabled:opacity-60">
            <Plus className="w-4 h-4" /> Dodaj područje
          </button>
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid={ADMIN.areasList}>
        {areas.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-10 text-center text-slate-500">Nema područja.</div>
        ) : areas.map((a) => (
          <div key={a.id} data-testid={ADMIN.areaRow} className="bg-white border border-slate-200 rounded-3xl p-5 flex items-start justify-between gap-3">
            <div>
              <div className="font-display font-bold flex items-center gap-2">
                {a.name}
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">/{a.slug}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {a.is_active ? 'Aktivno' : 'Skriveno'}
                </span>
              </div>
              <div className="text-sm text-slate-600 mt-1">{a.description}</div>
              {a.neighborhoods && <div className="text-xs text-slate-500 mt-1"><strong>Naselja:</strong> {a.neighborhoods}</div>}
            </div>
            <div className="flex flex-col gap-1.5">
              <button onClick={() => toggle(a)} className="btn-ghost px-3 py-1.5 rounded-full text-xs font-semibold">
                {a.is_active ? 'Sakrij' : 'Prikaži'}
              </button>
              <button data-testid={ADMIN.areaDelete} onClick={() => del(a.id)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Obriši
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Quotes ----------
function QuotesTab({ quotes }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6">
      {quotes.length === 0 ? <div className="text-slate-500 text-center py-8">Nema zahteva.</div> : (
        <div className="space-y-3">
          {quotes.map((q) => (
            <div key={q.id} className="p-4 rounded-2xl bg-slate-50">
              <div className="font-semibold">{q.name} <span className="text-slate-500 text-sm">· {q.email} · {q.phone}</span></div>
              <div className="text-sm text-slate-600 mt-1">Usluga: <strong>{q.service_type}</strong></div>
              {q.message && <div className="text-sm text-slate-700 mt-2">{q.message}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactMessagesTab({ messages }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6">
      {messages.length === 0 ? <div className="text-slate-500 text-center py-8">Nema poruka.</div> : (
        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.id} className="p-4 rounded-2xl bg-slate-50">
              <div className="font-semibold">{m.name} <span className="text-slate-500 text-sm">· {m.email}</span></div>
              <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{m.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Content & SEO ----------
function ContentTab({ content, setContent }) {
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put('/admin/site/content', content); setContent(data); setSavedAt(Date.now()); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 grid gap-4" data-testid={ADMIN.contentForm}>
      <Field label="Hero naslov" testid={ADMIN.contentHeroTitle} value={content.hero_title}
        onChange={(v) => setContent({...content, hero_title: v})} />
      <Field label="Hero podnaslov" testid={ADMIN.contentHeroSubtitle} textarea value={content.hero_subtitle}
        onChange={(v) => setContent({...content, hero_subtitle: v})} />
      <Field label="Hero značka" value={content.hero_badge} onChange={(v) => setContent({...content, hero_badge: v})} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="CTA primarno" value={content.cta_primary} onChange={(v) => setContent({...content, cta_primary: v})} />
        <Field label="CTA sekundarno" value={content.cta_secondary} onChange={(v) => setContent({...content, cta_secondary: v})} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Telefon" testid={ADMIN.contentPhone} value={content.phone} onChange={(v) => setContent({...content, phone: v})} />
        <Field label="Email" testid={ADMIN.contentEmail} value={content.email} onChange={(v) => setContent({...content, email: v})} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Instagram URL" testid={ADMIN.contentInstagram} value={content.instagram} onChange={(v) => setContent({...content, instagram: v})} />
        <Field label="Kontakt adresa (footer)" value={content.address} onChange={(v) => setContent({...content, address: v})} />
      </div>

      <div className="mt-2 border-t pt-4">
        <h4 className="font-display text-lg font-bold mb-3">Kako funkcioniše (4 koraka)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Naslov sekcije" value={content.steps_title} onChange={(v) => setContent({...content, steps_title: v})} />
          <Field label="Podnaslov" value={content.steps_subtitle} onChange={(v) => setContent({...content, steps_subtitle: v})} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {(content.steps || []).map((s, i) => (
            <div key={i} className="bg-slate-50 p-3 rounded-2xl space-y-2">
              <div className="flex items-center gap-2">
                <input value={s.n || ''}
                  onChange={(e) => setContent({...content, steps: content.steps.map((x, j) => j === i ? {...x, n: e.target.value} : x)})}
                  className="w-14 px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-bold" />
                <input value={s.t || ''}
                  onChange={(e) => setContent({...content, steps: content.steps.map((x, j) => j === i ? {...x, t: e.target.value} : x)})}
                  placeholder="Naslov koraka"
                  className="flex-1 px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold" />
              </div>
              <textarea rows={2} value={s.d || ''}
                onChange={(e) => setContent({...content, steps: content.steps.map((x, j) => j === i ? {...x, d: e.target.value} : x)})}
                placeholder="Kratak opis koraka"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm resize-none" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 border-t pt-4">
        <h4 className="font-display text-lg font-bold mb-3">Donji CTA banner</h4>
        <div className="grid grid-cols-1 gap-3">
          <Field label="Naslov" value={content.cta_bottom_title} onChange={(v) => setContent({...content, cta_bottom_title: v})} />
          <Field label="Podnaslov" textarea value={content.cta_bottom_subtitle} onChange={(v) => setContent({...content, cta_bottom_subtitle: v})} />
        </div>
      </div>

      <div className="mt-2 border-t pt-4">
        <h4 className="font-display text-lg font-bold mb-3">Naše vrednosti (na /o-nama)</h4>
        <Field label="Naslov sekcije" value={content.values_title} onChange={(v) => setContent({...content, values_title: v})} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {(content.values || []).map((v, i) => (
            <div key={i} className="bg-slate-50 p-3 rounded-2xl space-y-2">
              <input value={v.t || ''}
                onChange={(e) => setContent({...content, values: content.values.map((x, j) => j === i ? {...x, t: e.target.value} : x)})}
                placeholder="Naslov vrednosti"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold" />
              <textarea rows={2} value={v.d || ''}
                onChange={(e) => setContent({...content, values: content.values.map((x, j) => j === i ? {...x, d: e.target.value} : x)})}
                placeholder="Opis"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm resize-none" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 border-t pt-4">
        <h4 className="font-display text-lg font-bold mb-3">Brojevi poverenja (početna)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(content.stats || []).map((s, i) => (
            <div key={i} className="bg-slate-50 p-3 rounded-2xl space-y-2" data-testid={`content-stat-${i}`}>
              <div className="flex items-center gap-2">
                <input value={s.value ?? ''} type="number" step="0.1"
                  onChange={(e) => setContent({...content, stats: content.stats.map((x, j) => j === i ? {...x, value: e.target.value === '' ? '' : Number(e.target.value)} : x)})}
                  placeholder="500"
                  className="w-20 px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-bold" />
                <input value={s.suffix || ''}
                  onChange={(e) => setContent({...content, stats: content.stats.map((x, j) => j === i ? {...x, suffix: e.target.value} : x)})}
                  placeholder="+ / ★ / %"
                  className="w-16 px-2 py-1.5 rounded-lg border border-slate-300 text-sm" />
              </div>
              <input value={s.label || ''}
                onChange={(e) => setContent({...content, stats: content.stats.map((x, j) => j === i ? {...x, label: e.target.value} : x)})}
                placeholder="Opis"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg font-bold">Česta pitanja (FAQ)</h4>
          <button type="button" data-testid="content-faq-add"
            onClick={() => setContent({...content, faqs: [...(content.faqs || []), { q: '', a: '' }]})}
            className="text-sm font-semibold text-blue-700 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Dodaj pitanje</button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field label="Naslov sekcije" value={content.faq_title} onChange={(v) => setContent({...content, faq_title: v})} />
          <Field label="Podnaslov" value={content.faq_subtitle} onChange={(v) => setContent({...content, faq_subtitle: v})} />
        </div>
        <div className="grid grid-cols-1 gap-3 mt-3">
          {(content.faqs || []).map((f, i) => (
            <div key={i} className="bg-slate-50 p-3 rounded-2xl space-y-2" data-testid={`content-faq-${i}`}>
              <div className="flex items-center gap-2">
                <input value={f.q || ''}
                  onChange={(e) => setContent({...content, faqs: content.faqs.map((x, j) => j === i ? {...x, q: e.target.value} : x)})}
                  placeholder="Pitanje"
                  className="flex-1 px-2 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold" />
                <button type="button" aria-label="Obriši pitanje"
                  onClick={() => setContent({...content, faqs: content.faqs.filter((_, j) => j !== i)})}
                  className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
              </div>
              <textarea rows={2} value={f.a || ''}
                onChange={(e) => setContent({...content, faqs: content.faqs.map((x, j) => j === i ? {...x, a: e.target.value} : x)})}
                placeholder="Odgovor"
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm resize-none" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className="text-sm text-emerald-700">{savedAt ? `Sačuvano u ${new Date(savedAt).toLocaleTimeString()}` : ''}</span>
        <button type="submit" disabled={busy} data-testid={ADMIN.contentSave}
          className="btn-primary px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Sačuvaj
        </button>
      </div>
    </form>
  );
}

function SeoTab({ content, setContent }) {
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.put('/admin/site/content', content); setContent(data); setSavedAt(Date.now()); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 grid gap-4">
      <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 text-sm text-slate-700">
        <div className="flex items-start gap-2"><Search className="w-4 h-4 mt-0.5 text-blue-700" />
          <div>
            Ovde dodajete kodove za <strong>Google Search Console</strong>, <strong>Google Analytics</strong> i <strong>Google Business Profile</strong>.
            Sve promene utiču na celokupan sajt odmah. Sitemap je dostupan na <code className="bg-white px-1.5 py-0.5 rounded">/sitemap.xml</code>.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Google Analytics ID (npr. G-XXXXXX)" testid={ADMIN.seoGaId}
          value={content.ga_id} onChange={(v) => setContent({...content, ga_id: v})} />
        <Field label="Google Search Console verifikacija (meta token)" testid={ADMIN.seoGsc}
          value={content.gsc_verification} onChange={(v) => setContent({...content, gsc_verification: v})} />
      </div>

      <div className="border-t pt-4">
        <h4 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-600" /> Google Maps & Business Profile
        </h4>
        <p className="text-sm text-slate-600 mb-3">
          Sledeća polja povezuju vas sa Google Mapama i recenzijama. Detaljne instrukcije nalaze se u PDF uputstvu.
        </p>
        <div className="space-y-3">
          <Field
            label="Google Business Profile URL (link ka vašoj listi na Mapama)"
            testid={ADMIN.seoGbp}
            value={content.gbp_url}
            onChange={(v) => setContent({...content, gbp_url: v})}
            placeholder="https://maps.app.goo.gl/… ili https://g.co/kgs/…"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              label="Google Place ID (opciono — za recenzije preko API-ja)"
              value={content.google_place_id}
              onChange={(v) => setContent({...content, google_place_id: v})}
              placeholder="ChIJN1t_… (pronađite na Place ID Finder)"
            />
            <Field
              label="Google Maps API ključ (opciono — za live recenzije)"
              value={content.google_maps_api_key}
              onChange={(v) => setContent({...content, google_maps_api_key: v})}
              placeholder="AIza…"
            />
          </div>
          <Field
            label="Prilagođeni Google Maps embed URL (opciono — nadjačava adresu)"
            value={content.google_maps_embed_url}
            onChange={(v) => setContent({...content, google_maps_embed_url: v})}
            placeholder="https://www.google.com/maps/embed?pb=…"
          />
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs text-slate-600 space-y-1">
            <p><strong>Šta gde ide:</strong></p>
            <p>• <strong>GBP URL</strong> — dovoljno za dugmad „Otvori na Mapama" i „Ostavi recenziju" u Kontaktu i footeru.</p>
            <p>• <strong>Place ID + API ključ</strong> — potrebni SAMO ako želite da se prave Google recenzije prikazuju na sajtu (integracija sledeća faza).</p>
            <p>• <strong>Embed URL</strong> — nadjača podrazumevanu mapu koja se automatski gradi od adrese ispod.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Ulica i broj" testid={ADMIN.seoStreet}
          value={content.business_street} onChange={(v) => setContent({...content, business_street: v})} />
        <Field label="Poštanski broj" testid={ADMIN.seoPostal}
          value={content.business_postal} onChange={(v) => setContent({...content, business_postal: v})} />
        <Field label="Grad" value={content.business_city} onChange={(v) => setContent({...content, business_city: v})} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Geo Lat" testid={ADMIN.seoLat} type="number"
          value={content.business_lat} onChange={(v) => setContent({...content, business_lat: Number(v)})} />
        <Field label="Geo Lng" testid={ADMIN.seoLng} type="number"
          value={content.business_lng} onChange={(v) => setContent({...content, business_lng: Number(v)})} />
        <Field label="Radno vreme" testid={ADMIN.seoHours}
          value={content.opening_hours} onChange={(v) => setContent({...content, opening_hours: v})}
          placeholder="Mo-Sa 08:00-20:00" />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm text-emerald-700">{savedAt ? `Sačuvano u ${new Date(savedAt).toLocaleTimeString()}` : ''}</span>
        <button type="submit" disabled={busy} data-testid={ADMIN.seoSave}
          className="btn-primary px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Sačuvaj SEO
        </button>
      </div>
    </form>
  );
}


function ImagesTab({ content, setContent }) {
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const services = Array.isArray(content.services) ? content.services : [];

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const { data } = await api.put('/admin/site/content', content);
      setContent(data);
      setSavedAt(Date.now());
    } finally { setBusy(false); }
  };

  const updateService = (idx, patch) => {
    const next = services.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setContent({ ...content, services: next });
  };

  const updateBullets = (idx, value) => {
    const bullets = value.split('\n').map((b) => b.trim()).filter(Boolean);
    updateService(idx, { bullets });
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-images-form">
      <div className="bg-purple-50/60 border border-purple-100 rounded-2xl p-4 text-sm text-slate-700">
        <strong>Saveti:</strong> možete nalepiti URL slike (sa Unsplash, Pexels ili svog hostinga) ili učitati fajl
        sa vašeg računara. Učitane slike se čuvaju u bazi i prikazuju na sajtu odmah nakon klika na <em>Sačuvaj</em>.
        Max 6 MB po slici (jpg, png, webp, gif).
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold mb-4">Početna strana</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ImagePicker
            testId="admin-hero-image"
            label="Hero slika (desno na vrhu)"
            value={content.hero_image_url || ''}
            onChange={(v) => setContent({ ...content, hero_image_url: v })}
            height="h-44"
          />
          <ImagePicker
            testId="admin-about-image"
            label="Slika za 'O nama' stranicu"
            value={content.about_image_url || ''}
            onChange={(v) => setContent({ ...content, about_image_url: v })}
            height="h-44"
          />
          <ImagePicker
            testId="admin-before-image"
            label="Pre / Posle — leva (PRE)"
            value={content.before_image_url || ''}
            onChange={(v) => setContent({ ...content, before_image_url: v })}
            height="h-44"
          />
          <ImagePicker
            testId="admin-after-image"
            label="Pre / Posle — desna (POSLE)"
            value={content.after_image_url || ''}
            onChange={(v) => setContent({ ...content, after_image_url: v })}
            height="h-44"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold mb-1">Tri ključne usluge</h3>
        <p className="text-sm text-slate-600 mb-5">Naslov, opis, slika i lista detalja za svaku uslugu.
          Promene se odmah vide na <strong>Početna</strong> i <strong>Usluge</strong> stranicama.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((s, idx) => (
            <div key={s.key || idx} className="p-4 rounded-2xl border border-slate-200 bg-slate-50 space-y-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Usluga · /{s.key}</div>
              <input
                value={s.title || ''}
                onChange={(e) => updateService(idx, { title: e.target.value })}
                placeholder="Naslov"
                data-testid={`admin-service-title-${s.key}`}
                className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none focus:border-blue-700 text-sm font-semibold" />
              <textarea
                rows={3}
                value={s.description || ''}
                onChange={(e) => updateService(idx, { description: e.target.value })}
                placeholder="Kratak opis"
                data-testid={`admin-service-desc-${s.key}`}
                className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none focus:border-blue-700 text-sm resize-none" />
              <ImagePicker
                testId={`admin-service-image-${s.key}`}
                label="Slika usluge"
                value={s.image_url || ''}
                onChange={(v) => updateService(idx, { image_url: v })}
                height="h-36"
              />
              <div>
                <span className="block text-xs font-semibold text-slate-700 mb-1">Detalji (jedan po redu)</span>
                <textarea
                  rows={4}
                  value={(s.bullets || []).join('\n')}
                  onChange={(e) => updateBullets(idx, e.target.value)}
                  placeholder="Stavka 1&#10;Stavka 2&#10;Stavka 3"
                  data-testid={`admin-service-bullets-${s.key}`}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none focus:border-blue-700 text-xs resize-none" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between sticky bottom-2 bg-white/80 backdrop-blur rounded-2xl px-4 py-3 border border-slate-200">
        <span className="text-sm text-emerald-700">{savedAt ? `Sačuvano u ${new Date(savedAt).toLocaleTimeString()}` : 'Nesačuvane izmene'}</span>
        <button type="submit" disabled={busy} data-testid="admin-images-save"
          className="btn-primary px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Sačuvaj sve
        </button>
      </div>
    </form>
  );
}


function MarketingTab({ content, setContent }) {
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try { const { data } = await api.put('/admin/site/content', content); setContent(data); setSavedAt(Date.now()); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-marketing-form">
      <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-4 text-sm text-slate-700">
        <div className="flex items-start gap-2">
          <Megaphone className="w-4 h-4 mt-0.5 text-emerald-700" />
          <div>
            <strong>Marketing kontrolna tabla.</strong> Ovde uključujete promo kampanje, WhatsApp/Viber dugmad i
            povezujete Facebook Pixel + Google Ads praćenje konverzija.
          </div>
        </div>
      </div>

      {/* Promo */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold mb-1">Promo kod</h3>
        <p className="text-sm text-slate-600 mb-4">Kad je uključen, iznad hero-a se prikazuje traka sa kodom, a klijenti mogu da ga primene u poslednjem koraku rezervacije za automatski popust.</p>

        <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 mb-4">
          <div>
            <div className="font-semibold">Promo aktivan</div>
            <div className="text-xs text-slate-500">Kad je uključeno, banner + polje za kod su vidljivi</div>
          </div>
          <button
            type="button"
            onClick={() => setContent({ ...content, promo_active: !content.promo_active })}
            className={`relative w-12 h-7 rounded-full transition ${content.promo_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
            data-testid="admin-promo-toggle">
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${content.promo_active ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Promo kod" testid="admin-promo-code"
            value={content.promo_code}
            onChange={(v) => setContent({...content, promo_code: (v || '').toUpperCase()})}
            placeholder="PRVI15" />
          <Field label="Popust (%)" testid="admin-promo-percent" type="number"
            value={content.promo_percent}
            onChange={(v) => setContent({...content, promo_percent: parseInt(v || '0', 10)})} />
        </div>
        <div className="mt-3">
          <Field label="Tekst banera (Početna vrh stranice)" testid="admin-promo-banner"
            value={content.promo_banner_text}
            onChange={(v) => setContent({...content, promo_banner_text: v})}
            placeholder="Prvo čišćenje -15% — kod: PRVI15" />
        </div>
      </div>

      {/* WhatsApp / Viber */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold mb-1">WhatsApp & Viber</h3>
        <p className="text-sm text-slate-600 mb-4">Unesite brojeve u međunarodnom formatu, sa kodom zemlje bez + (npr. <code>381601234567</code>). Ako ostavite prazno, dugme se ne prikazuje.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="WhatsApp broj" testid="admin-whatsapp"
            value={content.whatsapp_number}
            onChange={(v) => setContent({...content, whatsapp_number: v})}
            placeholder="381601234567" />
          <Field label="Viber broj" testid="admin-viber"
            value={content.viber_number}
            onChange={(v) => setContent({...content, viber_number: v})}
            placeholder="381601234567" />
        </div>
      </div>

      {/* Pixels */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6">
        <h3 className="font-display text-xl font-bold mb-1">Google Ads & Facebook Pixel</h3>
        <p className="text-sm text-slate-600 mb-4">
          Sistem će automatski pucati konverzijske događaje kada klijent uspešno završi rezervaciju.
          Ubacite ID-jeve i sistem se aktivira sam.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Google Ads konverzija ID (AW-XXXXXXXX)" testid="admin-gads-id"
            value={content.google_ads_conversion_id}
            onChange={(v) => setContent({...content, google_ads_conversion_id: v})}
            placeholder="AW-1234567890" />
          <Field label="Google Ads label (opciono)" testid="admin-gads-label"
            value={content.google_ads_conversion_label}
            onChange={(v) => setContent({...content, google_ads_conversion_label: v})}
            placeholder="abc123DEF" />
        </div>
        <div className="mt-3">
          <Field label="Facebook Pixel ID" testid="admin-fb-pixel"
            value={content.facebook_pixel_id}
            onChange={(v) => setContent({...content, facebook_pixel_id: v})}
            placeholder="1234567890123456" />
        </div>
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs text-slate-600 space-y-1">
          <p><strong>Događaji koji se pucaju kad klijent zakaže:</strong></p>
          <p>• Google Ads: <code>conversion</code> + <code>booking_completed</code> sa vrednošću rezervacije u EUR</p>
          <p>• Facebook Pixel: <code>Lead</code> + <code>Schedule</code> sa vrednošću i valutom</p>
          <p>Na svim stranama se automatski puca <code>PageView</code>.</p>
        </div>
      </div>

      <div className="flex items-center justify-between sticky bottom-2 bg-white/80 backdrop-blur rounded-2xl px-4 py-3 border border-slate-200">
        <span className="text-sm text-emerald-700">{savedAt ? `Sačuvano u ${new Date(savedAt).toLocaleTimeString()}` : 'Nesačuvane izmene'}</span>
        <button type="submit" disabled={busy} data-testid="admin-marketing-save"
          className="btn-primary px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> Sačuvaj marketing
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, testid, textarea, placeholder, type = 'text' }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</span>
      {textarea ? (
        <textarea data-testid={testid} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          rows={3} placeholder={placeholder}
          className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 resize-none" />
      ) : (
        <input data-testid={testid} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
      )}
    </label>
  );
}
